// ---- Reads ---------------------------------------------------------------

import { useQuery, useQueries, keepPreviousData } from '@tanstack/react-query';
import { serviceApi, vmPath, vmKeys, unwrap, getPollingInterval, type WaitingState } from '../_shared';
import type {
  VmwareInstance,
  VmwareInstanceSummary,
  VmwareInstanceInfo,
  MonitoringQuery,
  MonitoringStats,
  RemoteTicket,
} from './instance-types';

/** VM list. `/instances` → `data.instances[]` (with optional `healthz`). */
export function useInstanceList(serviceId: string | undefined) {
  return useQuery({
    queryKey: vmKeys.instances(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ instances?: VmwareInstance[] } | VmwareInstance[]>(
        await serviceApi.get(vmPath(serviceId!, 'instances')),
      );
      return Array.isArray(body) ? body : body.instances ?? [];
    },
  });
}

/** Detail with adaptive polling while a dispatched action settles. */
export function useInstanceDetail(
  serviceId: string | undefined,
  instanceId: string | undefined,
  isWaitingUpdate: WaitingState = false,
) {
  return useQuery({
    queryKey: vmKeys.instance(serviceId ?? '', instanceId ?? ''),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    refetchInterval: (query) => {
      if (isWaitingUpdate) return getPollingInterval(isWaitingUpdate);
      const inst = query.state.data as VmwareInstance | undefined;
      if (!inst) return false;
      const key = String(inst.status ?? '').toUpperCase();
      const TERMINAL = ['POWERED_OFF', 'OFF', 'SUSPENDED', 'ERROR'];
      return TERMINAL.includes(key) ? false : 5000;
    },
    queryFn: async () => {
      const body = unwrap<{ instance?: VmwareInstance } | VmwareInstance>(
        await serviceApi.get(vmPath(serviceId!, `instances/${instanceId}`)),
      );
      return (body && typeof body === 'object' && 'instance' in body ? body.instance : body) as VmwareInstance;
    },
  });
}

/** Extended vSphere inventory (`/instances/:id/info` → `data.info`), 30s refresh. */
export function useInstanceInfo(serviceId: string | undefined, instanceId: string | undefined) {
  return useQuery({
    queryKey: vmKeys.instanceInfo(serviceId ?? '', instanceId ?? ''),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    refetchInterval: 30_000,
    queryFn: async () =>
      unwrap<{ info?: VmwareInstanceInfo }>(
        await serviceApi.get(vmPath(serviceId!, `instances/${instanceId}/info`)),
      ).info ?? {},
  });
}

/**
 * Status snapshot (`/instances/:id/summary` → `data.summary`).
 * Returns the full VmwareInstanceSummary including summaryItems, provider
 * moRef fields, and resourceIcons used by OverviewTab and the console URL builder.
 */
export function useInstanceSummary(serviceId: string | undefined, instanceId: string | undefined) {
  return useQuery({
    queryKey: vmKeys.instanceSummary(serviceId ?? '', instanceId ?? ''),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const raw = unwrap<{ summary?: VmwareInstanceSummary } | VmwareInstanceSummary>(
        await serviceApi.get(vmPath(serviceId!, `instances/${instanceId}/summary`)),
      );
      // The envelope may nest under `.summary` or be the root object.
      if (raw && typeof raw === 'object' && 'summary' in raw) {
        return (raw as { summary: VmwareInstanceSummary }).summary;
      }
      return raw as VmwareInstanceSummary;
    },
  });
}

/**
 * Batch detail fetch for the list page — hits `GET /instances/:id` for each id
 * so the list can show cpu/memory from `metadata`. React Query deduplicates
 * requests that overlap with the detail page cache (same queryKey).
 */
export function useInstanceDetails(
  serviceId: string | undefined,
  instanceIds: string[],
): Record<string, VmwareInstance> {
  const results = useQueries({
    queries: instanceIds.map((id) => ({
      queryKey: vmKeys.instance(serviceId ?? '', id),
      enabled: !!serviceId && !!id,
      retry: false,
      staleTime: 30_000,
      queryFn: async () => {
        const body = unwrap<{ instance?: VmwareInstance } | VmwareInstance>(
          await serviceApi.get(vmPath(serviceId!, `instances/${id}`)),
        );
        return (body && typeof body === 'object' && 'instance' in body ? body.instance : body) as VmwareInstance;
      },
    })),
  });
  const map: Record<string, VmwareInstance> = {};
  instanceIds.forEach((id, i) => {
    if (results[i].data) map[id] = results[i].data as VmwareInstance;
  });
  return map;
}

/** Batch summaries for a list page (one query per id under a single useQueries). */
export function useInstanceSummaries(
  serviceId: string | undefined,
  instanceIds: string[],
): Record<string, VmwareInstanceSummary> {
  const results = useQueries({
    queries: instanceIds.map((id) => ({
      queryKey: vmKeys.instanceSummary(serviceId ?? '', id),
      enabled: !!serviceId && !!id,
      retry: false,
      refetchInterval: 60_000,
      placeholderData: keepPreviousData,
      queryFn: async () => {
        const raw = unwrap<{ summary?: VmwareInstanceSummary } | VmwareInstanceSummary>(
          await serviceApi.get(vmPath(serviceId!, `instances/${id}/summary`)),
        );
        if (raw && typeof raw === 'object' && 'summary' in raw) {
          return (raw as { summary: VmwareInstanceSummary }).summary;
        }
        return raw as VmwareInstanceSummary;
      },
    })),
  });
  const map: Record<string, VmwareInstanceSummary> = {};
  instanceIds.forEach((id, i) => {
    map[id] = (results[i].data as VmwareInstanceSummary) ?? {};
  });
  return map;
}

/** Usage-trend series (`/instances/:id/stats` → `data.summary`), 60s refresh. */
export function useInstanceMonitoring(
  serviceId: string | undefined,
  instanceId: string | undefined,
  query?: MonitoringQuery,
) {
  return useQuery({
    queryKey: vmKeys.instanceMonitoring(serviceId ?? '', instanceId ?? '', query as Record<string, unknown> | undefined),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    refetchInterval: 60_000,
    queryFn: async () =>
      unwrap<{ summary?: MonitoringStats } | MonitoringStats>(
        await serviceApi.get(vmPath(serviceId!, `instances/${instanceId}/stats`), { params: query }),
      ),
  });
}

/**
 * WebMKS console ticket (`/instances/:id/acquire_ticket` → `data`).
 * Real response shape: `{ acquire: { serverInfo: { name, ... }, locale }, session }`.
 * The raw `data` field IS the ticket (no extra nesting after unwrap).
 */
export function useInstanceRemoteTicket(
  serviceId: string | undefined,
  instanceId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: vmKeys.instanceRemote(serviceId ?? '', instanceId ?? ''),
    enabled: enabled && !!serviceId && !!instanceId,
    retry: false,
    queryFn: async () =>
      unwrap<RemoteTicket>(await serviceApi.get(vmPath(serviceId!, `instances/${instanceId}/acquire_ticket`))),
  });
}
